import styled from "styled-components";
import {
  color_grey_dark,
  color_grey_light,
  color_grey_ultra_dark,
  color_grey_ultra_light,
  color_red_active,
  color_red_default,
  color_white,
} from "../../../constants/colors";

export const AuthForm = styled.form`
  width: 100%;
  max-width: ${(props) => (props.$compact ? "620px" : "380px")};
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$compact ? "16px" : "22px")};
  box-sizing: border-box;
  font-family: Onest, sans-serif;
`;

export const AuthFormHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: ${(props) => (props.$compact ? "0" : "10px")};
`;

export const AuthFormTitle = styled.h1`
  margin: 0;
  color: ${color_grey_ultra_dark};
  font: 700 36px/1.12 Onest, sans-serif;
  letter-spacing: -0.03em;

  @media (max-width: 640px) {
    font-size: 30px;
  }
`;

export const AuthFormSubtitle = styled.p`
  margin: 0;
  color: #737373;
  font: 400 14px/1.45 Onest, sans-serif;
`;

export const AuthFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const AuthFormRow = styled.div`
  display: grid;
  grid-template-columns: ${(props) => props.$columns ?? "1fr 1fr"};
  gap: 18px;
  align-items: ${(props) => props.$align ?? "start"};

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    align-items: stretch;
  }
`;

export const AuthField = styled.label`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 9px;
  color: ${color_grey_ultra_dark};
  font: 400 13px/1.3 Onest, sans-serif;

  input {
    width: 100%;
    height: 54px;
    padding: 0 16px;
    border: 0;
    border-radius: 12px;
    outline: none;
    background: #f3f3f3;
    color: ${color_grey_ultra_dark};
    box-sizing: border-box;
    font: 400 15px/1 Onest, sans-serif;
    transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease,
      background 0.15s ease;

    &::placeholder {
      color: ${color_grey_dark};
    }

    &:hover:not(:disabled) {
      background: #efefef;
    }

    &:focus {
      background: ${color_white};
      box-shadow: 0 0 0 2px rgba(240, 79, 79, 0.28);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.72;
    }
  }
`;

export const AuthLinkButton = styled.button`
  align-self: flex-end;
  margin-top: -10px;
  border: 0;
  padding: 0;
  background: transparent;
  color: ${color_red_default};
  font: 400 13px/1.3 Onest, sans-serif;
  cursor: pointer;

  &:hover {
    color: ${color_red_active};
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

export const AuthPrimaryButton = styled.button`
  width: 100%;
  height: 54px;
  border: 0;
  border-radius: 12px;
  background: ${(props) => (props.disabled ? color_grey_light : color_red_default)};
  color: ${color_white};
  font: 600 16px/1 Onest, sans-serif;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition:
    background 0.15s ease,
    transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover:not(:disabled) {
    background: ${color_red_active};
    box-shadow: 0 8px 20px rgba(240, 79, 79, 0.2);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 3px solid rgba(240, 79, 79, 0.25);
    outline-offset: 3px;
  }
`;

export const AuthSecondaryButton = styled(AuthPrimaryButton)`
  background: ${(props) => (props.disabled ? color_grey_light : color_red_default)};
`;

export const AuthPrompt = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  color: #737373;
  font: 400 13px/1.4 Onest, sans-serif;

  button {
    border: 0;
    padding: 0;
    background: transparent;
    color: ${color_red_default};
    font: inherit;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }
`;

export const RegisterFormShell = styled.div`
  width: min(100%, 620px);
`;

export const RegisterHelper = styled.p`
  margin: 6px 0 0;
  color: ${(props) => props.$color ?? color_grey_dark};
  font: 400 12px/1.35 Onest, sans-serif;
`;

export const ConsentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  color: ${color_grey_ultra_dark};
  font: 400 12px/1.4 Onest, sans-serif;

  a {
    color: ${color_red_default};
    text-underline-offset: 2px;
  }
`;

export const ConsentCheckbox = styled.label`
  position: relative;
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  border: 1px solid ${color_grey_light};
  border-radius: 4px;
  background: ${color_white};
  box-sizing: border-box;
  cursor: pointer;

  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  svg {
    position: absolute;
    inset: 4px 3px;
  }

  &:has(input:focus-visible) {
    outline: 3px solid rgba(240, 79, 79, 0.2);
    outline-offset: 2px;
  }
`;

export const PhoneFieldWrap = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;

  .FormPhoneInput-wrapper {
    width: 100% !important;
    gap: 9px !important;
    color: ${color_grey_ultra_dark};
    font: 400 13px/1.3 Onest, sans-serif;
  }

  .FormPhoneInput {
    width: 100% !important;
    min-width: 0 !important;
    max-width: none !important;
    height: 54px;
    border: 0 !important;
    border-radius: 12px !important;
    background: #f3f3f3 !important;
    box-sizing: border-box;
  }

  .PhoneInputInput {
    height: 52px !important;
    font-size: 15px !important;
  }

  .FormPhoneInput:focus-within {
    background: ${color_white} !important;
    box-shadow: 0 0 0 2px rgba(240, 79, 79, 0.28);
  }
`;

export const VisuallyMuted = styled.span`
  color: ${color_grey_dark};
`;
